/**
 * @fileoverview system-web System Status service — owns data access for the
 * dashboard (system status, status history, scaling trend) via the backend API.
 * @module system-web/services/systemStatus
 *
 * Responsibilities: - Fetch system status / scaling-trend from system-api and
 *                      expose typed accessors to hooks; own all API access
 * Dependencies:     - ../apiClient (apiFetch)
 * Architecture:     - Recognized inherently-online monitoring exception; the page
 *                      never calls the API directly — it goes service → hook → component
 * Notes:            - Live operational telemetry, not normal local-first user data.
 * @localFirst Source of Truth: server (live telemetry). Write Flow: read-only fetch.
 *   Sync Strategy: on-demand + polling via healthPollingService. Conflict Resolution: n/a.
 */
import { apiFetch } from '../apiClient';

// ---------------------------------------------------------------------------
// Types — mirror the backend response shape
// ---------------------------------------------------------------------------

export interface AtlasNode {
  host: string;
  role: 'PRIMARY' | 'SECONDARY' | 'ARBITER' | 'MONGOS' | string;
  cpuPercent: number;
  memPercent: number;
  diskUsedPercent: number;
  replicationLagSeconds: number;
  networkKBytesIn: number;
  networkKBytesOut: number;
}

export interface AtlasInfo {
  clusterName: string;
  tier: string;
  state: string;
  region: string;
  nodes: AtlasNode[];
}

export interface TenantClusterSummary {
  name: string;
  region: string;
  status: string;
  maxDatabases: number;
  currentDatabaseCount: number;
  atlas: AtlasInfo | null;
}

export interface MongoStats {
  uptime: number;
  uptimeFormatted: string;
  version: string;
  host: string;
  connections: { current: number; available: number; totalCreated: number };
  opcounters: { insert: number; query: number; update: number; delete: number; command: number };
  network: { bytesIn: number; bytesOut: number };
  mem: { resident: number; virtual: number };
  replication: { isReplSet: boolean; setName: string | null };
  systemDb: { collections: number; documents: number; dataSize: number; storageSize: number; indexSize: number };
  tenantDbCount: number;
  /** Shared tenant-data clusters registered for the CURRENT app environment only. */
  tenantClusters: TenantClusterSummary[];
}

export interface ApiHealth {
  name: string;
  url: string;
  status: 'healthy' | 'degraded' | 'down';
  latencyMs: number;
  checkedAt: string;
}

export type OverallHealth = 'healthy' | 'degraded' | 'critical';

/**
 * @contract MetricPoint
 * Purpose: One time-bucketed sparkline/trend value.
 * Fields:  t (ISO bucket start), v (numeric value).
 * Rules:   ordered ascending by time.
 * Sync:    n/a (live query result).
 */
export interface MetricPoint {
  t: string;
  v: number;
}

export interface ContainerAppStatus {
  name: string;
  status: 'running' | 'degraded' | 'down' | 'unknown';
  runningState: string;
  activeRevision: string;
  latestRevision: string;
  replicaCount: number;
  /** 24h Azure Monitor platform metrics; null/absent when unconfigured. */
  restartCount24h?: number | null;
  cpuUsageCores?: number | null;
  memoryUsageBytes?: number | null;
  rxBytes24h?: number | null;
  txBytes24h?: number | null;
  replicaSeries?: MetricPoint[];
}

export interface ScalingEvent {
  timestamp: string;
  app: string;
  reason: string;
  message: string;
}

export interface ContainerAppsStatus {
  environment: string;
  apps: ContainerAppStatus[];
  checkedAt: string;
  /** Recent scaling / KEDA / probe events; empty when Log Analytics unconfigured. */
  scalingEvents?: ScalingEvent[];
}

/**
 * @contract AppInsightsHealth
 * Purpose: Application Insights health-at-a-glance for the System Status page.
 * Fields:  request/failure/response/exception/dependency/page-load summaries +
 *          1h sparkline series + windowMinutes + checkedAt.
 * Rules:   null on the parent means "not configured"; series may be empty.
 * Sync:    n/a (live query result, not a synced record).
 */
export interface AppInsightsHealth {
  requestRatePerMin: number;
  failedRequestRatePct: number;
  avgResponseTimeMs: number;
  exceptionCount5m: number;
  dependencyFailureRatePct: number;
  pageLoadTimeMs: number | null;
  series: {
    requestsPerMin: MetricPoint[];
    failedRatePct: MetricPoint[];
    responseTimeMs: MetricPoint[];
    exceptions: MetricPoint[];
  };
  windowMinutes: number;
  checkedAt: string;
}

export interface SystemStatusData {
  collectedAt: string;
  environment: string;
  mongodb: MongoStats;
  apis: ApiHealth[];
  containerApps: ContainerAppsStatus | null;
  appInsights: AppInsightsHealth | null;
  overallHealth: OverallHealth;
}

export type ScalingTrendRange = '7d' | '30d' | '90d';

export interface ScalingTrendPoint {
  t: string;
  replicasAvg: number;
  replicasMax: number;
}

export interface ScalingTrend {
  range: ScalingTrendRange;
  granularity: 'hourly' | 'daily';
  trends: Record<string, ScalingTrendPoint[]>;
  checkedAt: string;
}

export interface StatusSnapshot {
  bucketAt: string;
  collectedAt: string;
  connections: { current: number; available: number };
  opcounters: { insert: number; query: number; update: number; delete: number; command: number };
  network: { bytesIn: number; bytesOut: number };
  mem: { resident: number; virtual: number };
  overallHealth: OverallHealth;
  tenantDbCount: number;
  atlasCpuPercent: number | null;
}

export interface StatusHistory {
  snapshots: StatusSnapshot[];
}

// ---------------------------------------------------------------------------
// Service functions — real-time data; valid Dexie bypass (inherently online)
// ---------------------------------------------------------------------------

export async function getSystemStatus(): Promise<SystemStatusData> {
  return apiFetch('/api/system/status') as Promise<SystemStatusData>;
}

export async function getStatusHistory(): Promise<StatusHistory> {
  return apiFetch('/api/system/status/history') as Promise<StatusHistory>;
}

export async function getScalingTrend(range: ScalingTrendRange): Promise<ScalingTrend> {
  return apiFetch(`/api/system/status/scaling-trend?range=${range}`) as Promise<ScalingTrend>;
}

// ---------------------------------------------------------------------------
// Tenant sync health — feeds the Sync Health dashboard. Phase 1: returns the
// real tenant list with platform status; per-tenant sync metrics (last sync,
// pending/failed/conflict counts, WS connection state) arrive in Phase 2.
// ---------------------------------------------------------------------------

/**
 * @contract TenantSyncHealthRecord
 * Purpose: One row per tenant in the Sync Health dashboard.
 * Fields:  _id, tenantSlug, tenantName, healthStatus, sseConnected,
 *          lastSyncAt, lastSuccessfulSyncAt, pendingPushCount,
 *          failedPushCount, unresolvedConflictCount, activeCollections,
 *          totalCollections, errorMessage, updatedAt.
 * Rules:   healthStatus is derived from tenant platform status + sync ledger
 *          signals via the six-state machine in system-api's
 *          services/tenantSyncHealthStatus.ts — degraded/error assert a
 *          problem; idle/undetermined assert absence of evidence.
 * Sync:    n/a (live monitoring read, inherently online).
 */
export interface TenantSyncHealthRecord {
  _id: string;
  tenantSlug: string;
  tenantName: string;
  healthStatus: 'healthy' | 'degraded' | 'error' | 'offline' | 'idle' | 'undetermined';
  sseConnected: boolean;
  /** Phase 2: per-tenant last sync timestamp. Absent in Phase 1. */
  lastSyncAt?: string;
  /** Phase 2: per-tenant last successful sync timestamp. Absent in Phase 1. */
  lastSuccessfulSyncAt?: string;
  pendingPushCount: number;
  failedPushCount: number;
  unresolvedConflictCount: number;
  activeCollections: number;
  totalCollections: number;
  /** Phase 2: per-tenant last sync error message. Absent in Phase 1. */
  errorMessage?: string;
  updatedAt: string;
}

export interface TenantSyncHealthResponse {
  tenants: TenantSyncHealthRecord[];
  activeAdminConnections: number;
  checkedAt: string;
}

export async function getTenantSyncHealth(): Promise<TenantSyncHealthResponse> {
  return apiFetch('/api/sync/tenant-health') as Promise<TenantSyncHealthResponse>;
}
